// Always available (not DEV-gated) — this is a static fixture page, harmless in production.
// It is intentionally NOT linked from any navigation; only reachable by direct URL.
// Playwright CI tests may use this route even in production build mode.
import { useSearchParams } from 'react-router-dom';
import { SceneExperienceRenderer } from '../experiences/scene/SceneExperienceRenderer';
import { royalManifestFixture } from '../experiences/scene/fixtures/royal-1';
import type { SceneManifest } from '../experiences/scene/manifest';
import { extractCoupleNames, safeFormatDateLong } from '../../infrastructure/lib/weddingDataHelper';

const minimalImage = {
  publicUrl: '/assets/marathi/backdrop.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 0,
  dimensions: { width: 1024, height: 1024 },
  provider: 'licensed_psd',
};

const utsavImage = {
  publicUrl: '/assets/marathi/peacock.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 0,
  dimensions: { width: 1024, height: 1024 },
  provider: 'licensed_psd',
};

const utsavHeroPoster = {
  publicUrl: '/assets/royal-ganesh-interactive/black.png',
  mimeType: 'image/png',
  sizeBytes: 0,
  dimensions: { width: 1080, height: 1920 },
  provider: 'licensed_psd',
};

const utsavHeroVideo = {
  publicUrl: '/assets/royal-ganesh-interactive/Fireworks - Black Screen - Free Download.mp4',
  mimeType: 'video/mp4',
  sizeBytes: 0,
  dimensions: { width: 1920, height: 1080 },
  durationSeconds: 8,
  provider: 'licensed_psd',
};

const minimalFixture: SceneManifest = {
  ...royalManifestFixture,
  invitationId: 'fixture-minimal-luxe',
  template: 'minimal',
  opening: { enabled: true, title: 'A celebration of love', subtitle: 'Tap to enter', skipStorageKey: 'invitecloud:opened:fixture-minimal-luxe' },
  typography: { nameTreatment: 'editorial_serif', foil: 'champagne', monogram: 'PA' },
  hero: {
    ...royalManifestFixture.hero,
    background: { ...royalManifestFixture.hero.background, primaryImage: minimalImage },
    decor: { intensity: 'subtle' },
  },
  sections: royalManifestFixture.sections.map(section => ({
    ...section,
    assets: { ...section.assets, primaryImage: minimalImage },
    decor: { intensity: 'subtle' },
  })),
};

const utsavFixture: SceneManifest = {
  ...royalManifestFixture,
  invitationId: 'fixture-utsav-vibrant',
  template: 'modern_vibrant',
  opening: { enabled: true, title: 'The celebration begins', subtitle: 'Tap to enter', skipStorageKey: 'invitecloud:opened:fixture-utsav-vibrant' },
  typography: { nameTreatment: 'festival_script', foil: 'peacock', monogram: 'PA' },
  hero: {
    ...royalManifestFixture.hero,
    background: {
      ...royalManifestFixture.hero.background,
      primaryImage: utsavHeroPoster,
      primaryVideo: utsavHeroVideo,
      renderStrategy: 'video_primary',
      particleEffect: 'none',
    },
    decor: { intensity: 'ceremonial' },
  },
  sections: royalManifestFixture.sections.map(section => ({
    ...section,
    assets: { ...section.assets, primaryImage: utsavImage },
    decor: { intensity: 'subtle' },
  })),
};

const rajasthaniRoyalImage = {
  publicUrl: '/assets/wow/rajasthani_palace_bg.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 0,
  dimensions: { width: 1920, height: 1080 },
  provider: 'licensed_psd',
};

const rajasthaniRoyalHeroVideo = {
  publicUrl: '/assets/wow/rajasthani_palace_hero_alive_compressed.mp4',
  mimeType: 'video/mp4',
  sizeBytes: 0,
  dimensions: { width: 1344, height: 768 },
  durationSeconds: 5,
  provider: 'fal_ai_minimax',
};

const RAJASTHANI_TITLES: Record<string, string> = {
  engagement: 'Sagai Dastoor',
  haldi: 'Pithhi Dastoor',
  antarpat: 'Gathbandhan & Antarpat',
  varmala: 'Toran & Varmala',
  saptapadi: 'Phere (Sacred Vows)',
};

const RAJASTHANI_IMAGES: Record<string, string> = {
  engagement: '/assets/wow/rajasthani_sagai.jpg',
  haldi: '/assets/wow/rajasthani_pithhi.jpg',
  antarpat: '/assets/wow/rajasthani_antarpat.jpg',
  varmala: '/assets/wow/rajasthani_varmala.jpg',
  saptapadi: '/assets/wow/rajasthani_phere.jpg',
};

const RAJASTHANI_VIDEOS: Record<string, string> = {
  engagement: '/assets/wow/rajasthani_sagai_alive_compressed.mp4',
  haldi: '/assets/wow/rajasthani_pithhi_alive_compressed.mp4',
  antarpat: '/assets/wow/rajasthani_antarpat_alive_compressed.mp4',
  varmala: '/assets/wow/rajasthani_varmala_alive_compressed.mp4',
  saptapadi: '/assets/wow/rajasthani_phere_alive_compressed.mp4',
};

const RAJASTHANI_ANIMATIONS: Record<string, { entry: any; parallax: { enabled: boolean; intensity: number }; scrollTriggerThreshold: number }> = {
  engagement: { entry: 'zoom_in', parallax: { enabled: true, intensity: 0.6 }, scrollTriggerThreshold: 0.2 },
  haldi: { entry: 'fade_up', parallax: { enabled: true, intensity: 0.4 }, scrollTriggerThreshold: 0.2 },
  antarpat: { entry: 'curtain_reveal', parallax: { enabled: true, intensity: 0.5 }, scrollTriggerThreshold: 0.2 },
  varmala: { entry: 'zoom_in', parallax: { enabled: true, intensity: 0.7 }, scrollTriggerThreshold: 0.2 },
  saptapadi: { entry: 'zoom_in', parallax: { enabled: true, intensity: 0.5 }, scrollTriggerThreshold: 0.2 },
};

const RAJASTHANI_INTERACTIVE: Record<string, { kind: any }> = {
  engagement: { kind: 'corridor' },
  haldi: { kind: 'living' },
  antarpat: { kind: 'living' },
  varmala: { kind: 'corridor' },
  saptapadi: { kind: 'petal_shower' },
};

const rajasthaniRoyalFixture: SceneManifest = {
  ...royalManifestFixture,
  invitationId: 'fixture-rajasthani-royal',
  template: 'heritage',
  opening: { enabled: true, title: 'Banna & Banni Wedding', subtitle: 'Tap to enter Rajput Royal Kankotri', skipStorageKey: 'invitecloud:opened:fixture-rajasthani-royal' },
  typography: { nameTreatment: 'calligraphy_reveal', foil: 'gold', monogram: 'BB' },
  hero: {
    ...royalManifestFixture.hero,
    primaryName: 'Banna',
    secondaryName: 'Banni',
    tagline: 'Padharo Mhare Des — A Royal Rajput Wedding',
    background: {
      ...royalManifestFixture.hero.background,
      primaryImage: rajasthaniRoyalImage,
      primaryVideo: rajasthaniRoyalHeroVideo,
      renderStrategy: 'video_primary',
    },
    decor: { intensity: 'ornate' },
  },
  sections: royalManifestFixture.sections.map(section => {
    const bgUrl = RAJASTHANI_IMAGES[section.momentId] ?? rajasthaniRoyalImage.publicUrl;
    const videoUrl = RAJASTHANI_VIDEOS[section.momentId];
    return {
      ...section,
      content: {
        ...section.content,
        title: RAJASTHANI_TITLES[section.momentId] ?? section.content.title,
      },
      animation: RAJASTHANI_ANIMATIONS[section.momentId] ?? section.animation,
      interactive: RAJASTHANI_INTERACTIVE[section.momentId] ?? section.interactive,
      mobileBehavior: { disableVideo: false, reduceParticles: false },
      assets: {
        ...section.assets,
        primaryImage: {
          ...rajasthaniRoyalImage,
          publicUrl: bgUrl,
        },
        ...(videoUrl
          ? {
              primaryVideo: {
                publicUrl: videoUrl,
                mimeType: 'video/mp4',
                sizeBytes: 0,
                dimensions: { width: 1344, height: 768 },
                durationSeconds: 5,
                provider: 'fal_ai_minimax',
              },
              renderStrategy: 'video_primary' as const,
            }
          : {}),
      },
      decor: { intensity: 'subtle' },
    };
  }),
};

// ─── Indo-Ghibli Wedding Fixture ─────────────────────────────────────────────
const ghibliHeroImage = {
  publicUrl: '/assets/ghibli/ghibli_palace_hero.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 0,
  dimensions: { width: 1024, height: 768 },
  provider: 'fal_ai_flux',
};

const ghibliHeroVideo = {
  publicUrl: '/assets/ghibli/ghibli_palace_hero_alive_compressed.mp4',
  mimeType: 'video/mp4',
  sizeBytes: 0,
  dimensions: { width: 1024, height: 768 },
  durationSeconds: 7,
  provider: 'fal_ai_minimax',
};

const GHIBLI_IMAGES: Record<string, string> = {
  engagement: '/assets/ghibli/ghibli_sagai.jpg',
  haldi: '/assets/ghibli/ghibli_haldi.jpg',
  antarpat: '/assets/ghibli/ghibli_antarpat.jpg',
  varmala: '/assets/ghibli/ghibli_varmala.jpg',
  saptapadi: '/assets/ghibli/ghibli_phere.jpg',
};

const GHIBLI_VIDEOS: Record<string, string> = {
  engagement: '/assets/ghibli/ghibli_sagai_alive_compressed.mp4',
  haldi: '/assets/ghibli/ghibli_haldi_alive_compressed.mp4',
  antarpat: '/assets/ghibli/ghibli_antarpat_alive_compressed.mp4',
  varmala: '/assets/ghibli/ghibli_varmala_alive_compressed.mp4',
  saptapadi: '/assets/ghibli/ghibli_phere_alive_compressed.mp4',
};

const GHIBLI_TITLES: Record<string, string> = {
  engagement: 'The Promise — Sagai Dastoor',
  haldi: 'Golden Sunlight — Pithhi Haldi',
  antarpat: 'Sacred Knot — Gathbandhan',
  varmala: 'Floral Garland — Varmala Mahurat',
  saptapadi: 'Eternal Vows — Saat Phere',
};

const ghibliWeddingFixture: SceneManifest = {
  ...rajasthaniRoyalFixture,
  invitationId: 'fixture-ghibli-wedding',
  template: 'indo_ghibli',
  opening: {
    enabled: true,
    title: 'Rahul & Priya Royal Vivah',
    subtitle: 'Tap to enter our Studio Ghibli Fairytale',
    skipStorageKey: 'invitecloud:opened:fixture-ghibli-wedding',
  },
  typography: { nameTreatment: 'calligraphy_reveal', foil: 'gold', monogram: 'RP' },
  hero: {
    ...rajasthaniRoyalFixture.hero,
    primaryName: 'Rahul',
    secondaryName: 'Priya',
    tagline: 'A Magical Royal Fairytale by the Lake',
    background: {
      ...rajasthaniRoyalFixture.hero.background,
      primaryImage: ghibliHeroImage,
      primaryVideo: ghibliHeroVideo,
      renderStrategy: 'video_primary',
      particleEffect: 'rose_petals',
    },
    decor: { intensity: 'ornate' },
  },
  sections: rajasthaniRoyalFixture.sections.map((section) => {
    const bgUrl = GHIBLI_IMAGES[section.momentId] ?? ghibliHeroImage.publicUrl;
    const videoUrl = GHIBLI_VIDEOS[section.momentId];
    return {
      ...section,
      content: {
        ...section.content,
        title: GHIBLI_TITLES[section.momentId] ?? section.content.title,
      },
      interactive: { kind: 'living' },
      mobileBehavior: { disableVideo: false, reduceParticles: false },
      assets: {
        ...section.assets,
        primaryImage: {
          ...ghibliHeroImage,
          publicUrl: bgUrl,
        },
        ...(videoUrl
          ? {
              primaryVideo: {
                ...ghibliHeroVideo,
                publicUrl: videoUrl,
                durationSeconds: 7,
              },
              renderStrategy: 'video_primary' as const,
            }
          : {
              renderStrategy: 'image_primary' as const,
            }),
      },
    };
  }),
};



// ─── Pichwai Heritage Wedding Fixture ─────────────────────────────────────────
const pichwaiHeroImage = {
  publicUrl: '/assets/pichwai/pichwai_palace_hero.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 0,
  dimensions: { width: 1024, height: 768 },
  provider: 'fal_ai_flux',
};

const pichwaiHeroVideo = {
  publicUrl: '/assets/pichwai/pichwai_palace_hero_alive_compressed.mp4',
  mimeType: 'video/mp4',
  sizeBytes: 0,
  dimensions: { width: 1024, height: 768 },
  durationSeconds: 7,
  provider: 'fal_ai_minimax',
};

const PICHWAI_IMAGES: Record<string, string> = {
  engagement: '/assets/pichwai/pichwai_sagai.jpg',
  haldi: '/assets/pichwai/pichwai_haldi.jpg',
  antarpat: '/assets/pichwai/pichwai_antarpat.jpg',
  varmala: '/assets/pichwai/pichwai_varmala.jpg',
  saptapadi: '/assets/pichwai/pichwai_phere.jpg',
};

const PICHWAI_VIDEOS: Record<string, string> = {
  engagement: '/assets/pichwai/pichwai_sagai_alive_compressed.mp4',
  haldi: '/assets/pichwai/pichwai_haldi_alive_compressed.mp4',
  antarpat: '/assets/pichwai/pichwai_antarpat_alive_compressed.mp4',
  varmala: '/assets/pichwai/pichwai_varmala_alive_compressed.mp4',
  saptapadi: '/assets/pichwai/pichwai_phere_alive_compressed.mp4',
};

const PICHWAI_TITLES: Record<string, string> = {
  engagement: 'The Royal Engagement — Sagai Dastoor',
  haldi: 'Sacred Turmeric Blessings — Pithhi Dastoor',
  antarpat: 'The Sacred Veil — Shubh Antarpat',
  varmala: 'The Royal Garland Exchange — Varmala Mahurat',
  saptapadi: 'The Sacred Seven Vows — Saat Phere',
};

const pichwaiWeddingFixture: SceneManifest = {
  ...rajasthaniRoyalFixture,
  invitationId: 'fixture-pichwai-wedding',
  template: 'pichwai_heritage',
  opening: {
    enabled: true,
    title: 'Mewar Pichwai Shubh Vivah',
    subtitle: 'Tap to enter the Sacred Rajput Kankotri',
    skipStorageKey: 'invitecloud:opened:fixture-pichwai-wedding',
  },
  typography: { nameTreatment: 'calligraphy_reveal', foil: 'gold', monogram: 'AA' },
  hero: {
    ...rajasthaniRoyalFixture.hero,
    primaryName: 'Aditya',
    secondaryName: 'Ananya',
    tagline: 'Sacred Union Under Royal Palace Stars',
    background: {
      ...rajasthaniRoyalFixture.hero.background,
      primaryImage: pichwaiHeroImage,
      primaryVideo: pichwaiHeroVideo,
      renderStrategy: 'video_primary',
      particleEffect: 'sparkles',
    },
    decor: { intensity: 'ornate' },
  },
  sections: rajasthaniRoyalFixture.sections.map((section) => {
    const bgUrl = PICHWAI_IMAGES[section.momentId] ?? pichwaiHeroImage.publicUrl;
    const videoUrl = PICHWAI_VIDEOS[section.momentId];
    return {
      ...section,
      content: {
        ...section.content,
        title: PICHWAI_TITLES[section.momentId] ?? section.content.title,
      },
      interactive: { kind: 'living' },
      mobileBehavior: { disableVideo: false, reduceParticles: false },
      assets: {
        ...section.assets,
        primaryImage: {
          ...pichwaiHeroImage,
          publicUrl: bgUrl,
        },
        ...(videoUrl
          ? {
              primaryVideo: {
                ...pichwaiHeroVideo,
                publicUrl: videoUrl,
                durationSeconds: 5,
              },
              renderStrategy: 'video_primary' as const,
            }
          : {
              renderStrategy: 'image_primary' as const,
            }),
      },
    };
  }),
};

// ─── Storybook 3D Wedding Fixture ─────────────────────────────────────────────
const storybookHeroImage = {
  publicUrl: '/assets/storybook/storybook_palace_hero.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 0,
  dimensions: { width: 1024, height: 768 },
  provider: 'fal_ai_flux',
};

const storybookHeroVideo = {
  publicUrl: '/assets/storybook/storybook_palace_hero_alive_compressed.mp4',
  mimeType: 'video/mp4',
  sizeBytes: 0,
  dimensions: { width: 1024, height: 768 },
  durationSeconds: 7,
  provider: 'fal_ai_minimax',
};

const STORYBOOK_IMAGES: Record<string, string> = {
  engagement: '/assets/storybook/storybook_sagai.jpg',
  haldi: '/assets/storybook/storybook_haldi.jpg',
  antarpat: '/assets/storybook/storybook_antarpat.jpg',
  varmala: '/assets/storybook/storybook_varmala.jpg',
  saptapadi: '/assets/storybook/storybook_phere.jpg',
};

const STORYBOOK_VIDEOS: Record<string, string> = {
  engagement: '/assets/storybook/storybook_sagai_alive_compressed.mp4',
  haldi: '/assets/storybook/storybook_haldi_alive_compressed.mp4',
  antarpat: '/assets/storybook/storybook_antarpat_alive_compressed.mp4',
  varmala: '/assets/storybook/storybook_varmala_alive_compressed.mp4',
  saptapadi: '/assets/storybook/storybook_phere_alive_compressed.mp4',
};

const STORYBOOK_TITLES: Record<string, string> = {
  engagement: 'The Promise Begins — Sagai Celebration',
  haldi: 'Sunlit Joy — Haldi Ceremony',
  antarpat: 'A Magical Glimpse — Sacred Antarpat',
  varmala: 'Under The Starlit Sky — Varmala',
  saptapadi: 'Forever & Always — Saat Phere',
};

const storybook3dWeddingFixture: SceneManifest = {
  ...rajasthaniRoyalFixture,
  invitationId: 'fixture-storybook3d-wedding',
  template: 'storybook_3d',
  opening: {
    enabled: true,
    title: 'A Modern Royal Fairytale',
    subtitle: 'Tap to step into our 3D Storybook',
    skipStorageKey: 'invitecloud:opened:fixture-storybook3d-wedding',
  },
  typography: { nameTreatment: 'calligraphy_reveal', foil: 'gold', monogram: 'RI' },
  hero: {
    ...rajasthaniRoyalFixture.hero,
    primaryName: 'Rohan',
    secondaryName: 'Isha',
    tagline: 'Once Upon A Time, Forever Begins',
    background: {
      ...rajasthaniRoyalFixture.hero.background,
      primaryImage: storybookHeroImage,
      primaryVideo: storybookHeroVideo,
      renderStrategy: 'video_primary',
      particleEffect: 'fireflies',
    },
    decor: { intensity: 'ornate' },
  },
  sections: rajasthaniRoyalFixture.sections.map((section) => {
    const bgUrl = STORYBOOK_IMAGES[section.momentId] ?? storybookHeroImage.publicUrl;
    const videoUrl = STORYBOOK_VIDEOS[section.momentId];
    return {
      ...section,
      content: {
        ...section.content,
        title: STORYBOOK_TITLES[section.momentId] ?? section.content.title,
      },
      interactive: { kind: 'living' },
      mobileBehavior: { disableVideo: false, reduceParticles: false },
      assets: {
        ...section.assets,
        primaryImage: {
          ...storybookHeroImage,
          publicUrl: bgUrl,
        },
        ...(videoUrl
          ? {
              primaryVideo: {
                ...storybookHeroVideo,
                publicUrl: videoUrl,
                durationSeconds: 5,
              },
              renderStrategy: 'video_primary' as const,
            }
          : {
              renderStrategy: 'image_primary' as const,
            }),
      },
    };
  }),
};

const superheroImage = {
  publicUrl: '/assets/birthday/ironman_cinematic_hero.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 0,
  dimensions: { width: 1920, height: 1080 },
  provider: 'antigravity_ai',
};

const cyberpunkCakeImage = {
  publicUrl: '/assets/birthday/cyberpunk_luxury_cake.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 0,
  dimensions: { width: 1920, height: 1080 },
  provider: 'antigravity_ai',
};

const cyberpunkPenthouseLoungeImage = {
  publicUrl: '/assets/birthday/cyberpunk_penthouse_lounge.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 0,
  dimensions: { width: 1920, height: 1080 },
  provider: 'antigravity_ai',
};

const cyberpunkRobotPartyImage = {
  publicUrl: '/assets/birthday/cyberpunk_robot_party.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 0,
  dimensions: { width: 1920, height: 1080 },
  provider: 'antigravity_ai',
};

const cyberpunkHologramGiftImage = {
  publicUrl: '/assets/birthday/cyberpunk_hologram_gift.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 0,
  dimensions: { width: 1920, height: 1080 },
  provider: 'antigravity_ai',
};

const cyberpunkSkylineFireworksImage = {
  publicUrl: '/assets/birthday/cyberpunk_skyline_fireworks.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 0,
  dimensions: { width: 1920, height: 1080 },
  provider: 'antigravity_ai',
};

const ironmanCandleVideo = {
  publicUrl: '/assets/birthday/ironman_candle_compressed.mp4',
  mimeType: 'video/mp4',
  sizeBytes: 0,
  dimensions: { width: 1280, height: 720 },
  durationSeconds: 10,
  provider: 'antigravity_ai',
};

const superheroHallImage = {
  publicUrl: '/assets/birthday/ironman_stark_hall.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 0,
  dimensions: { width: 1920, height: 1080 },
  provider: 'antigravity_ai',
};

const superheroCelebrationImage = {
  publicUrl: '/assets/birthday/ironman_birthday_celebration_real.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 0,
  dimensions: { width: 1920, height: 1080 },
  provider: 'antigravity_ai',
};


const birthdaySuperheroFixture: SceneManifest = {
  ...royalManifestFixture,
  invitationId: 'fixture-birthday-superhero',
  template: 'midnight_glow',
  opening: { enabled: true, title: 'Hero Assembly Initiated', subtitle: 'Tap to Launch Party Mission', skipStorageKey: 'invitecloud:opened:fixture-birthday-superhero' },
  typography: { nameTreatment: 'festival_script', foil: 'gold', monogram: 'AV' },
  hero: {
    ...royalManifestFixture.hero,
    primaryName: 'Alexander',
    secondaryName: '10th Birthday Blast',
    tagline: 'Assemble at Stark Tower Observatory for the Ultimate Birthday Mission!',
    background: {
      renderStrategy: 'video_primary',
      primaryImage: cyberpunkCakeImage,
      primaryVideo: ironmanCandleVideo,
      particleEffect: 'none',
    },
    decor: { intensity: 'ceremonial' },
  },
  sections: [
    {
      sectionId: 'sec-hero-launch',
      momentId: 'opening',
      order: 0,
      content: {
        title: 'Stark Observatory Assembly',
        subtitle: 'The Ultimate Superhero Mission Begins',
        date: '2026-08-15',
        time: '5:00 PM',
        venue: { name: 'Cosmic Stark Tower Arena', address: '100 Superhero Boulevard' },
        customNote: 'Costumes & Superpowers Encouraged!',
      },
      assets: {
        renderStrategy: 'image_primary',
        primaryImage: cyberpunkCakeImage,
        particleEffect: 'none',
      },
      animation: { entry: 'zoom_in', parallax: { enabled: true, intensity: 0.5 }, scrollTriggerThreshold: 0.2 },
      mobileBehavior: { disableVideo: false, reduceParticles: false },
    },
    {
      sectionId: 'sec-cyber-lounge',
      momentId: 'party',
      order: 1,
      content: {
        title: 'Cyberpunk Android Lounge Party',
        subtitle: 'High-Tech Observatory Deck Celebration',
        date: '2026-08-15',
        time: '6:30 PM',
        venue: { name: 'High-Tech Penthouse Deck', address: '100 Cybernetics Avenue' },
        customNote: 'Immersive Neon Android & Robot VIP Seating',
      },
      assets: {
        renderStrategy: 'image_primary',
        primaryImage: cyberpunkRobotPartyImage,
        particleEffect: 'sparkles',
      },
      animation: { entry: 'fade_up', parallax: { enabled: true, intensity: 0.4 }, scrollTriggerThreshold: 0.3 },
      mobileBehavior: { disableVideo: false, reduceParticles: false },
      interactive: { kind: 'depth' }, // Clean parallax depth without buttons
    },
    {
      sectionId: 'sec-cyber-gift',
      momentId: 'ritual',
      order: 2,
      content: {
        title: 'Holographic Gift & Cake Ceremony',
        subtitle: 'Living LED Circuit & Plasma Glow Stage',
        date: '2026-08-15',
        time: '7:30 PM',
        venue: { name: 'Sci-Fi Innovation Lab Stage', address: 'Tower Floor 99' },
        customNote: 'Experience the Living Holographic Glow',
      },
      assets: {
        renderStrategy: 'image_primary',
        primaryImage: cyberpunkHologramGiftImage,
        particleEffect: 'embers',
      },
      animation: { entry: 'fade_up', parallax: { enabled: true, intensity: 0.5 }, scrollTriggerThreshold: 0.3 },
      mobileBehavior: { disableVideo: false, reduceParticles: false },
      interactive: { kind: 'living' }, // Automatic glowing particle effect
    },
    {
      sectionId: 'sec-cyber-fireworks',
      momentId: 'story',
      order: 3,
      content: {
        title: 'Cyberpunk Skyline Fireworks Finale',
        subtitle: 'Breathtaking 360 Metropolis Balcony View',
        date: '2026-08-15',
        time: '9:00 PM',
        venue: { name: 'Skyline Balcony Deck', address: 'Top Floor Sky Deck' },
        customNote: 'Tilt or Drag to Explore the Skyline Horizon',
      },
      assets: {
        renderStrategy: 'image_primary',
        primaryImage: cyberpunkSkylineFireworksImage,
        particleEffect: 'sparkles',
      },
      animation: { entry: 'depth_zoom', parallax: { enabled: true, intensity: 0.6 }, scrollTriggerThreshold: 0.3 },
      mobileBehavior: { disableVideo: false, reduceParticles: false },
      interactive: { kind: 'panorama' }, // Wide-angle skyline panning
    },
  ],
  footer: {
    venue: { name: 'Cosmic Stark Tower Arena', address: '100 Superhero Boulevard' },
    rsvpLink: 'https://invitecloud.in/rsvp/superhero',
    calendar: {
      title: 'Alexander 10th Birthday Blast',
      startsAt: '2026-08-15T17:00:00+05:30',
      endsAt: '2026-08-15T21:00:00+05:30',
    },
  },
};

const darkKnightImage = {
  publicUrl: '/assets/birthday/dark_knight_hero.svg',
  mimeType: 'image/svg+xml',
  sizeBytes: 0,
  dimensions: { width: 1920, height: 1080 },
  provider: 'antigravity_ai',
};

const batmanJokerBirthdayImage = {
  publicUrl: '/assets/birthday/batman_joker_birthday.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 985050,
  dimensions: { width: 1920, height: 1080 },
  provider: 'antigravity_ai',
};

const jokerSparklersVideo = {
  publicUrl: '/assets/birthday/joker_sparklers_compressed.mp4',
  mimeType: 'video/mp4',
  sizeBytes: 0,
  dimensions: { width: 1280, height: 720 },
  durationSeconds: 10,
  provider: 'antigravity_ai',
};

const jokerBatmanCakeImage = {
  publicUrl: '/assets/birthday/joker_batman_cake.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 1159563,
  dimensions: { width: 1920, height: 1080 },
  provider: 'antigravity_ai',
};

const batmanCakeVideo = {
  publicUrl: '/assets/birthday/batman_cake_compressed.mp4',
  mimeType: 'video/mp4',
  sizeBytes: 0,
  dimensions: { width: 1280, height: 720 },
  durationSeconds: 10,
  provider: 'antigravity_ai',
};

const batmobileAlleyImage = {
  publicUrl: '/assets/birthday/batmobile_birthday_alley.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 1048331,
  dimensions: { width: 1920, height: 1080 },
  provider: 'antigravity_ai',
};

const birthdayDarkKnightFixture: SceneManifest = {
  ...royalManifestFixture,
  invitationId: 'fixture-birthday-dark-knight',
  template: 'modern_noir',
  opening: { enabled: true, title: 'Ethan\'s 15th Birthday', subtitle: 'Tap to Authorize Batcave Entry Pass', skipStorageKey: 'invitecloud:opened:fixture-birthday-dark-knight' },
  typography: { nameTreatment: 'editorial_serif', foil: 'champagne', monogram: 'EK' },
  hero: {
    ...royalManifestFixture.hero,
    primaryName: 'Ethan\'s',
    secondaryName: '15th Birthday',
    tagline: 'YOU ARE SUMMONED TO ETHAN\'S GOTHAM CITY BIRTHDAY BASH',
    eventDate: 'September 2, 2026',
    background: {
      renderStrategy: 'video_primary',
      primaryImage: batmanJokerBirthdayImage,
      primaryVideo: batmanCakeVideo, // Swapped to put the main cake video in the first place
      particleEffect: 'none',
    },
    decor: { intensity: 'ornate' },
  },
  sections: [
    {
      sectionId: 'sec-batcave-vault',
      momentId: 'opening',
      order: 0,
      content: {
        title: 'Ethan\'s Batcave Command Vault',
        subtitle: 'Gotham Hero Entry Authorized',
        date: '2026-09-02',
        time: '7:00 PM',
        venue: { name: 'Gotham Knight Vault', address: 'Wayne Manor Sub-Level 3' },
        customNote: 'Strictly Cyber & Gotham Noir Attire',
      },
      assets: {
        renderStrategy: 'image_primary',
        primaryImage: batmanJokerBirthdayImage,
        particleEffect: 'none',
      },
      animation: { entry: 'curtain_reveal', parallax: { enabled: true, intensity: 0.3 }, scrollTriggerThreshold: 0.2 },
      mobileBehavior: { disableVideo: false, reduceParticles: false },
      interactive: { kind: 'portals' },
    },
    {
      sectionId: 'sec-candle-blowout',
      momentId: 'countdown',
      order: 1,
      content: {
        title: 'Ethan\'s Birthday Cake Blowout',
        subtitle: 'Batarang Candle Lighter Ceremony',
        date: '2026-09-02',
        time: '8:00 PM',
        venue: { name: 'Gotham Sky Deck', address: 'Skyscraper Altar' },
        customNote: 'Make a Wish & Light the Gotham Candles',
      },
      assets: {
        renderStrategy: 'video_primary',
        primaryImage: jokerBatmanCakeImage,
        primaryVideo: jokerSparklersVideo, // Swapped to be the secondary video
        particleEffect: 'none',
      },
      animation: { entry: 'fade_up', parallax: { enabled: true, intensity: 0.4 }, scrollTriggerThreshold: 0.3 },
      mobileBehavior: { disableVideo: false, reduceParticles: false },
      interactive: { kind: 'countdown_flip' },
    },
  ],
  footer: {
    venue: { name: 'Gotham Knight Vault', address: 'Wayne Manor Sub-Level 3' },
    rsvpLink: 'https://invitecloud.in/rsvp/dark-knight',
    calendar: {
      title: 'Ethan 15th Birthday Bash',
      startsAt: '2026-09-02T19:00:00+05:30',
      endsAt: '2026-09-02T22:00:00+05:30',
    },
  },
};

const barbieCastleImage = {
  publicUrl: '/assets/birthday/barbie_princess_castle.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 1009541,
  dimensions: { width: 1920, height: 1080 },
  provider: 'antigravity_ai',
};

const barbieCastleVideo = {
  publicUrl: '/assets/birthday/Fairytale_castle_at_twilight_202607241302_compressed.mp4',
  mimeType: 'video/mp4',
  sizeBytes: 0,
  dimensions: { width: 1280, height: 720 },
  durationSeconds: 10,
  provider: 'antigravity_ai',
};

const barbieTeaPartyImage = {
  publicUrl: '/assets/birthday/barbie_tea_party.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 1038480,
  dimensions: { width: 1920, height: 1080 },
  provider: 'antigravity_ai',
};

const fairyMagicGiftsImage = {
  publicUrl: '/assets/birthday/fairy_magic_gifts.jpg',
  mimeType: 'image/jpeg',
  sizeBytes: 979247,
  dimensions: { width: 1920, height: 1080 },
  provider: 'antigravity_ai',
};

const birthdayWonderlandFixture: SceneManifest = {
  ...royalManifestFixture,
  invitationId: 'fixture-birthday-wonderland',
  template: 'pastel_dream',
  opening: { enabled: true, title: 'Mia\'s 5th Birthday Party', subtitle: 'Tap to Enter the Fairy Castle', skipStorageKey: 'invitecloud:opened:fixture-birthday-wonderland' },
  typography: { nameTreatment: 'festival_script', foil: 'rose', monogram: 'MB' },
  hero: {
    ...royalManifestFixture.hero,
    primaryName: 'Mia\'s',
    secondaryName: '5th Birthday',
    tagline: 'WELCOME TO MIA\'S MAGICAL FAIRY PRINCESS PARTY',
    eventDate: 'October 10, 2026',
    background: {
      renderStrategy: 'video_primary',
      primaryImage: barbieCastleImage,
      primaryVideo: barbieCastleVideo,
      particleEffect: 'none', // Removed balloons so the video's native physics shine
    },
    decor: { intensity: 'subtle' },
  },
  sections: [
    {
      sectionId: 'sec-fairy-gate',
      momentId: 'opening',
      order: 0,
      content: {
        title: 'Enchanted Fairy Castle Gate',
        subtitle: 'Where Princess Dreams & Balloons Float',
        date: '2026-10-10',
        time: '4:00 PM',
        venue: { name: 'Fairy Castle Garden', address: 'Rainbow Cloud Lane' },
        customNote: 'Magic Wands & Princess Wings Provided!',
      },
      assets: {
        renderStrategy: 'image_primary',
        primaryImage: barbieCastleImage,
        particleEffect: 'none',
      },
      animation: { entry: 'zoom_in', parallax: { enabled: true, intensity: 0.4 }, scrollTriggerThreshold: 0.2 },
      mobileBehavior: { disableVideo: false, reduceParticles: false },
      interactive: { kind: 'lift_veil' },
    },
    {
      sectionId: 'sec-fairy-living',
      momentId: 'party',
      order: 1,
      content: {
        title: 'Mia\'s Fairy Tea Party & Cake Cutting',
        subtitle: 'Living Petals & Birthday Wish Stage',
        date: '2026-10-10',
        time: '5:00 PM',
        venue: { name: 'Grand Cake Castle Stage', address: 'Rainbow Cloud Lane' },
        customNote: 'Candy Cascades & Fairy Princess Dance',
      },
      assets: {
        renderStrategy: 'image_primary',
        primaryImage: barbieTeaPartyImage,
        particleEffect: 'rose_petals',
      },
      animation: { entry: 'fade_up', parallax: { enabled: true, intensity: 0.5 }, scrollTriggerThreshold: 0.3 },
      mobileBehavior: { disableVideo: false, reduceParticles: false },
      interactive: { kind: 'living' },
    },
    {
      sectionId: 'sec-fairy-magic-gifts',
      momentId: 'story',
      order: 2,
      content: {
        title: 'Fairy Magic Wand & Gift Unboxing',
        subtitle: 'Magical Wishes & Surprise Present Opening',
        date: '2026-10-10',
        time: '6:00 PM',
        venue: { name: 'Royal Gift Balcony', address: 'Rainbow Cloud Lane' },
        customNote: 'Open Your Princess Fairy Surprise Box',
      },
      assets: {
        renderStrategy: 'image_primary',
        primaryImage: fairyMagicGiftsImage,
        particleEffect: 'sparkles',
      },
      animation: { entry: 'zoom_in', parallax: { enabled: true, intensity: 0.4 }, scrollTriggerThreshold: 0.3 },
      mobileBehavior: { disableVideo: false, reduceParticles: false },
      interactive: { kind: 'tilt_card' },
    },
  ],
  footer: {
    venue: { name: 'Fairy Castle Garden', address: 'Rainbow Cloud Lane' },
    rsvpLink: 'https://invitecloud.in/rsvp/fairy-mia',
    calendar: {
      title: 'Mia 5th Magical Fairy Princess Party',
      startsAt: '2026-10-10T16:00:00+05:30',
      endsAt: '2026-10-10T19:00:00+05:30',
    },
  },
};

export const FIXTURES: Record<string, SceneManifest> = {
  'royal-heritage': royalManifestFixture,
  'minimal-luxe': minimalFixture,
  'utsav-vibrant': utsavFixture,
  'rajasthani-royal': rajasthaniRoyalFixture,
  'birthday-superhero': birthdaySuperheroFixture,
  'birthday-dark-knight': birthdayDarkKnightFixture,
  'birthday-wonderland': birthdayWonderlandFixture,
  'exp-birthday-superhero-v1': birthdaySuperheroFixture,
  'exp-birthday-dark-knight-v1': birthdayDarkKnightFixture,
  'exp-birthday-wonderland-v1': birthdayWonderlandFixture,
  // Restored original 3 AI birthday templates — use closest style-pack fixture for preview
  'exp-birthday-minimal-v1': minimalFixture,
  'exp-birthday-premium-v1': birthdaySuperheroFixture,   // premium_thematic — cinematic gold/crimson
  'exp-birthday-vibrant-v1': birthdayWonderlandFixture,  // modern_vibrant — energetic festive
  'exp-cinematic-royal-v1': royalManifestFixture,
  'exp-cinematic-minimal-v1': minimalFixture,
  'exp-cinematic-fantasy-v1': utsavFixture,
  'exp-wedding-rajasthani-royal-v1': rajasthaniRoyalFixture,
  'exp-wedding-indo-ghibli-v1': ghibliWeddingFixture,
  'pichwai-wedding': pichwaiWeddingFixture,
  'exp-wedding-pichwai-v1': pichwaiWeddingFixture,
  'storybook-3d-wedding': storybook3dWeddingFixture,
  'exp-wedding-storybook-3d-v1': storybook3dWeddingFixture,
  'royal-1': royalManifestFixture,
  'minimal-1': minimalFixture,
};

/**
 * Occasion-appropriate fallback when a template has no bespoke fixture.
 *
 * Previously every miss fell through to the royal *wedding* manifest, so a birthday
 * template previewed as a wedding. Showing the wrong occasion is worse than showing
 * something plain, so keep this keyed by occasion.
 */
const OCCASION_FALLBACK_FIXTURE: Record<string, SceneManifest> = {
  WEDDING: royalManifestFixture,
  BIRTHDAY: birthdayWonderlandFixture,
};

/**
 * A template may be referenced by id, by slug, or by the whole API object.
 *
 * Both matter: fixtures are keyed by authored slug-style ids, but templates loaded from
 * the database arrive with UUID `id`s. Looking up by id alone always missed for DB-backed
 * rows and silently returned a wedding manifest.
 */
export type ExperienceTemplateRef =
  | string
  | { id?: string; slug?: string; occasionType?: string };

export function resolveExperienceManifest(
  templateRef: ExperienceTemplateRef,
  invitationData?: Record<string, any> | null
): SceneManifest {
  const ref = typeof templateRef === 'string' ? { id: templateRef } : (templateRef ?? {});
  const candidates = [ref.id, ref.slug].filter(Boolean) as string[];

  const matched = candidates.map(key => FIXTURES[key]).find(Boolean);

  if (!matched && import.meta.env?.DEV) {
    console.warn(
      `[experience-preview] No fixture for template ${JSON.stringify(candidates)}; ` +
      `falling back to the ${ref.occasionType ?? 'WEDDING'} default. ` +
      `Preview will not reflect what publishing generates.`,
    );
  }

  const baseFixture =
    matched
    ?? OCCASION_FALLBACK_FIXTURE[(ref.occasionType ?? '').toUpperCase()]
    ?? royalManifestFixture;

  if (!invitationData) {
    return baseFixture;
  }

  // AI-native experience templates (ExperienceDetailsStep.tsx) write into
  // `customData.experience.meta`, not `customData.birthday` — that key belongs
  // to the separate, code-rendered birthday templates' editor (StepDetails.tsx).
  // Reading the wrong key here meant the live wizard preview for every
  // aiNative birthday template (e.g. exp-birthday-wonderland-v1) always showed
  // the hardcoded fixture's name/venue/tagline, never the user's own input.
  const customDataAny = (invitationData.customData as any) || {};
  const expMeta = customDataAny.experience?.meta || {};
  const weddingData = customDataAny.wedding || {};
  const birthdayData = customDataAny.birthday || {};

  const isWedding =
    (ref.occasionType ?? '').toUpperCase() === 'WEDDING' ||
    (invitationData.occasionType ?? '').toUpperCase() === 'WEDDING' ||
    Boolean(weddingData.groom || weddingData.bride || weddingData.weddingEvents) ||
    Boolean(invitationData.groomName || invitationData.brideName) ||
    baseFixture.template === 'heritage' ||
    ((baseFixture as any).meta)?.stylePack === 'heritage' ||
    baseFixture.hero.primaryName === 'Banna' ||
    (baseFixture.opening?.title ?? '').toLowerCase().includes('wedding');

  if (isWedding) {
    const couple = extractCoupleNames(invitationData.eventName);
    const groomName =
      weddingData.groom?.fullName ||
      weddingData.groom?.name ||
      invitationData.groomName ||
      expMeta.groomName ||
      couple?.groom ||
      (invitationData.eventName && !couple && invitationData.eventName.includes('&')
        ? invitationData.eventName.split('&')[0].trim()
        : '') ||
      baseFixture.hero.primaryName ||
      'Rahul';

    const brideName =
      weddingData.bride?.fullName ||
      weddingData.bride?.name ||
      invitationData.brideName ||
      expMeta.brideName ||
      couple?.bride ||
      (invitationData.eventName && !couple && invitationData.eventName.includes('&')
        ? invitationData.eventName.split('&')[1].trim()
        : '') ||
      baseFixture.hero.secondaryName ||
      'Priya';

    const hostName =
      invitationData.hostName ||
      weddingData.hostNames ||
      weddingData.hosts?.[0]?.name;

    const venueName =
      weddingData.venueName ||
      weddingData.venue?.name ||
      invitationData.eventVenue ||
      baseFixture.footer?.venue?.name;

    const venueAddress =
      weddingData.venueAddress ||
      weddingData.venue?.line ||
      '';

    const customMessage =
      expMeta.tagline ||
      invitationData.customMessage ||
      weddingData.tagline ||
      baseFixture.hero.tagline;

    const couplePhotoUrl =
      weddingData.couplePhotoUrl ||
      weddingData.groom?.photoUrl ||
      weddingData.bride?.photoUrl ||
      invitationData.mediaUrls?.[0] ||
      expMeta.personPhotoUrl ||
      (baseFixture.hero as any).personPhotoUrl;

    const formattedDate = invitationData.eventDate
      ? safeFormatDateLong(invitationData.eventDate, 'en-US', baseFixture.hero.eventDate)
      : baseFixture.hero.eventDate;

    const monogramLetters = `${(groomName[0] || 'R').toUpperCase()}${(brideName[0] || 'P').toUpperCase()}`;

    // Extract user ceremonies if provided
    const userCeremonies = Array.isArray(invitationData.ceremonies) && invitationData.ceremonies.length > 0
      ? invitationData.ceremonies
      : Array.isArray(weddingData.ceremonies) && weddingData.ceremonies.length > 0
        ? weddingData.ceremonies
        : Array.isArray(weddingData.weddingEvents) && weddingData.weddingEvents.length > 0
          ? weddingData.weddingEvents
          : null;

    return {
      ...baseFixture,
      typography: {
        ...baseFixture.typography,
        monogram: monogramLetters,
      },
      opening: baseFixture.opening
        ? {
            ...baseFixture.opening,
            title: `${groomName} & ${brideName} Wedding`,
            subtitle: baseFixture.opening.subtitle || 'Tap to enter Rajput Royal Kankotri',
          }
        : undefined,
      hero: {
        ...baseFixture.hero,
        primaryName: groomName,
        secondaryName: brideName,
        tagline: hostName ? `${customMessage} • Hosted by ${hostName}` : customMessage,
        eventDate: formattedDate,
        personPhotoUrl: couplePhotoUrl,
      },
      sections: baseFixture.sections.map((sec, idx) => {
        const userEvent = userCeremonies ? userCeremonies[idx] : null;
        return {
          ...sec,
          content: {
            ...sec.content,
            title: userEvent?.title || userEvent?.name || sec.content.title,
            date: userEvent?.date ? safeFormatDateLong(userEvent.date, 'en-US', formattedDate) : (formattedDate || sec.content.date),
            time: userEvent?.time || sec.content.time,
            venue: {
              name: userEvent?.venue || venueName || sec.content.venue.name,
              address: userEvent?.address || venueAddress || (venueName ? '' : sec.content.venue.address),
            },
            customNote: userEvent?.description || customMessage || sec.content.customNote,
          },
        };
      }),
      footer: {
        ...baseFixture.footer,
        venue: {
          name: venueName || baseFixture.footer?.venue?.name || 'Grand Palace Venue',
          address: venueAddress || (venueName ? '' : baseFixture.footer?.venue?.address || 'Palace Road, Jaipur'),
        },
      },
    };
  }

  // AI-native experience templates (ExperienceDetailsStep.tsx) write into
  // `customData.experience.meta`, not `customData.birthday` — that key belongs
  // to the separate, code-rendered birthday templates' editor (StepDetails.tsx).
  // Reading the wrong key here meant the live wizard preview for every
  // aiNative birthday template (e.g. exp-birthday-wonderland-v1) always showed
  // the hardcoded fixture's name/venue/tagline, never the user's own input.
  const personName =
    expMeta.primaryName ||
    birthdayData.birthdayPersonName ||
    invitationData.eventName ||
    baseFixture.hero.primaryName ||
    'Birthday Star';

  const ageTurning = birthdayData.ageTurning;
  const secondaryTitle = ageTurning
    ? `Turning ${ageTurning}`
    : baseFixture.hero.secondaryName;

  const hostName =
    invitationData.hostName || birthdayData.hostNames;

  const venueName =
    invitationData.eventVenue || birthdayData.venueName || baseFixture.footer?.venue?.name;

  // No dedicated "address" field exists in either editor today (both collect a
  // single combined "Venue Name & Address" string) — fall back to blank rather
  // than the fixture's hardcoded address, since showing a fixture's fake
  // address alongside the user's own venue name is worse than omitting it.
  const venueAddress = birthdayData.venueAddress || '';

  const eventTime = birthdayData.eventTime || '5:30 PM';
  const customMessage =
    expMeta.tagline ||
    invitationData.customMessage ||
    birthdayData.celebrationTagline ||
    baseFixture.hero.tagline;

  const personPhotoUrl =
    expMeta.personPhotoUrl || expMeta.referenceImages?.primary || undefined;

  const formattedDate = invitationData.eventDate
    ? new Date(invitationData.eventDate).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : baseFixture.hero.eventDate;

  return {
    ...baseFixture,
    opening: baseFixture.opening
      ? {
          ...baseFixture.opening,
          title: `${personName}'s ${ageTurning ? `Turning ${ageTurning} ` : ''}Birthday`,
          subtitle: `Tap to open ${personName}'s celebration invitation`,
        }
      : undefined,
    hero: {
      ...baseFixture.hero,
      primaryName: personName,
      secondaryName: secondaryTitle,
      tagline: hostName ? `${customMessage} • Hosted by ${hostName}` : customMessage,
      eventDate: formattedDate,
      personPhotoUrl: personPhotoUrl || (baseFixture.hero as any).personPhotoUrl,
    },
    sections: baseFixture.sections.map((sec, idx) => {
      if (idx === 0) {
        return {
          ...sec,
          content: {
            ...sec.content,
            title: `${personName}'s Birthday Party Assembly`,
            date: formattedDate || sec.content.date,
            time: eventTime,
            venue: {
              name: venueName || sec.content.venue.name,
              // Only fall back to the fixture's address when the venue name itself
              // is also still the fixture's — i.e. the user hasn't entered a real
              // venue at all. Once they have, an unrelated fixture address
              // (e.g. "Rainbow Cloud Lane") must never be shown next to it.
              address: venueAddress || (venueName ? '' : sec.content.venue.address),
            },
            customNote: customMessage || sec.content.customNote,
          },
        };
      }
      return sec;
    }),
    footer: {
      ...baseFixture.footer,
      venue: {
        name: venueName || baseFixture.footer?.venue?.name || 'Celebration Arena',
        address: venueAddress || (venueName ? '' : baseFixture.footer?.venue?.address || 'City Center'),
      },
    },
  };
}

function withQaOpeningKey(fixture: SceneManifest, key: string | null): SceneManifest {
  if (!key || !fixture.opening) return fixture;
  return {
    ...fixture,
    opening: {
      ...fixture.opening,
      skipStorageKey: `${fixture.opening.skipStorageKey ?? fixture.invitationId}:${key}`,
    },
  };
}

export function ExperiencePreview() {
  const [params] = useSearchParams();
  const fixtureName = params.get('fixture') ?? 'royal-heritage';
  const showOpening = params.get('opening') === '1';
  let fixture = FIXTURES[fixtureName] ?? royalManifestFixture;

  if (!showOpening) {
    fixture = {
      ...fixture,
      opening: fixture.opening ? { ...fixture.opening, enabled: false } : undefined,
    };
  } else {
    fixture = withQaOpeningKey(fixture, params.get('qa'));
  }

  return <SceneExperienceRenderer manifest={fixture} preview={!showOpening} />;
}
